import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import tmdbApi from "../../api/tmdbApi";

let { appname } = window.app;

// How many times to silently re-check while Stripe is still verifying, and how
// far apart. Purely a convenience so the client doesn't have to click a button
// during what's usually a very short async gap.
const AUTO_POLL_ATTEMPTS = 5;
const AUTO_POLL_INTERVAL_MS = 4000;

// Stripe's account_onboarding Account Link "return_url" lands here once the client
// finishes (or abandons) the hosted onboarding flow. Reaching this page does NOT
// mean onboarding actually succeeded - Stripe still has to verify what was
// submitted, so we ask the backend for the connected account's real status
// (which it gets from Stripe directly / keeps in sync via the account.updated webhook)
// instead of trusting the redirect alone.
const StripeOnboardingReturn = () => {
  const navigate = useNavigate();
  const [image, setImg] = useState(process.env.REACT_APP_IMAGE_CLOUD_FRONT);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);
  const [resuming, setResuming] = useState(false);
  const pollCount = useRef(0);
  const pollTimer = useRef(null);

  const nextRoute = localStorage.getItem("stripeOnboardingNextRoute") || "/thankyou";
  // Set by ConnectBankAccount.jsx before redirecting to Stripe: true when this
  // step's next stop is Add Store. Wording only - Add Store is not gated on the
  // bank connection, so "later" is always a real option.
  const mandatory = localStorage.getItem("stripeOnboardingMandatory") === "1";
  const hasRedirected = useRef(false);
  const redirectTimer = useRef(null);

  useEffect(() => {
    if (window.site === undefined) {
      setTimeout(() => {
        if (window?.site?.common?.imageCloudfront) {
          setImg(window.site.common.imageCloudfront);
        }
      }, 1000);
    }
    if (window?.site?.common?.imageCloudfront) {
      setImg(window.site.common.imageCloudfront);
    }
  }, [window.site]);

  const checkStatus = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(false);
    try {
      const response = await tmdbApi.getStripeConnectStatus();
      if (tmdbApi.isSessionExpired(response)) {
        // Don't auto-poll against a session that's gone - show the retry state.
        setError(true);
        return;
      }
      setStatus(response?.result || response);
    } catch (err) {
      console.error("getStripeConnectStatus error:", err);
      setError(true);
      if (!silent) toast.error("Could not check your bank account status.", { position: "bottom-center" });
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    // Stripe's return_url no longer carries a clientid, and this page no longer reads
    // one: the status call identifies the client from the session token, so a link
    // pasted or replayed by someone else reports on their own account, not this one.
    checkStatus();
    return () => {
      clearTimeout(pollTimer.current);
      clearTimeout(redirectTimer.current);
    };
  }, []);

  // While details were submitted but Stripe hasn't flipped payoutsEnabled on yet,
  // keep quietly re-checking for a bit instead of making the client click Refresh.
  useEffect(() => {
    const detailsSubmitted = status?.detailsSubmitted === true;
    const payoutsEnabled = status?.payoutsEnabled === true;
    if (!detailsSubmitted || payoutsEnabled || error) return;
    if (pollCount.current >= AUTO_POLL_ATTEMPTS) return;

    pollTimer.current = setTimeout(() => {
      pollCount.current += 1;
      checkStatus({ silent: true });
    }, AUTO_POLL_INTERVAL_MS);

    return () => clearTimeout(pollTimer.current);
  }, [status, error]);

  // Continues to nextRoute - usually "/addstore", where the client was heading
  // before this detour. Safe whether or not Stripe finished: Add Store no longer
  // redirects back here when payouts aren't enabled (see editStore.jsx), so this
  // can't restart the old Connect Bank <-> Add Store loop.
  const handleContinue = () => {
    const destination = localStorage.getItem("stripeOnboardingNextRoute") || "/dashboard";
    localStorage.removeItem("stripeOnboardingNextRoute");
    localStorage.removeItem("stripeOnboardingMandatory");
    navigate(destination);
  };

  // Don't make the client click "Continue" once we know the bank account is
  // actually connected - move them on to the next step automatically.
  useEffect(() => {
    if (status?.payoutsEnabled === true && !hasRedirected.current) {
      hasRedirected.current = true;
      redirectTimer.current = setTimeout(() => {
        handleContinue();
      }, 1800);
    }
  }, [status]);

  // Straight back to Stripe, not through the ConnectBankAccount explainer page again -
  // the client has already seen it once, and Stripe's Account Links resume the hosted
  // flow exactly where they left off for the same connected account.
  const handleResume = async () => {
    setResuming(true);
    try {
      const response = await tmdbApi.createStripeConnectOnboarding();
      const result = response?.result || response;
      const onboardingUrl = result?.onboardingUrl;

      if (!onboardingUrl) {
        toast.error("Could not resume bank account setup. Please try again.", { position: "bottom-center" });
        setResuming(false);
        return;
      }

      localStorage.setItem("stripeOnboardingNextRoute", nextRoute);
      localStorage.setItem("stripeOnboardingMandatory", mandatory ? "1" : "");
      window.location.href = onboardingUrl;
    } catch (err) {
      console.error("createStripeConnectOnboarding (resume) error:", err);
      toast.error("Could not resume bank account setup. Please try again.", { position: "bottom-center" });
      setResuming(false);
    }
  };

  const payoutsEnabled = status?.payoutsEnabled === true;
  const detailsSubmitted = status?.detailsSubmitted === true;

  // One card, four states. The tone class swaps an accent colour through a CSS
  // custom property (media circle + status pill), so the state reads at a glance
  // without repeating the card chrome in every branch below.
  const tone = error
    ? "is-error"
    : payoutsEnabled
    ? "is-success"
    : !loading && detailsSubmitted
    ? "is-verifying"
    : "";
  const icon = payoutsEnabled ? "check_circle" : error ? "error_outline" : "account_balance";
  const pillLabel = loading
    ? "Checking status"
    : error
    ? "Status unavailable"
    : payoutsEnabled
    ? "Connected"
    : detailsSubmitted
    ? "Verifying with Stripe"
    : "Setup incomplete";
  // Pulses only while something is genuinely still in flight - our own status
  // check, or Stripe's verification that the poll above is waiting on.
  const pillPulsing = loading || (detailsSubmitted && !payoutsEnabled && !error);

  return (
    <div className="stripe-flow-page" id="page-top">
      <ToastContainer position="bottom-right" id="toastContainer" />
      <div className="container" id="stripe-return-container">
        <div className="signin-wrapper" id="signin-wrapper">
          <img
            src={`${image}${appname}/images/web/logo-dark.png`}
            className="logo"
            alt="logo"
            id="stripe-return-logo"
            style={{ cursor: "pointer" }}
            onClick={() => navigate("/dashboard")}
          />
          <div className="stripe-flow-body" id="stripe-return-section">
            <div className={`stripe-flow-card ${tone}`.trim()} id="stripe-return-card">
              <div className="stripe-flow-card__media" id="stripe-return-icon-col">
                <span className="material-icons" aria-hidden="true">{icon}</span>
              </div>
              <div className="stripe-flow-card__content" id="stripe-return-right-column">
                <span className="stripe-flow-pill" id="stripe-return-pill">
                  <span className={`stripe-flow-pill__dot${pillPulsing ? " is-pulsing" : ""}`} />
                  {pillLabel}
                </span>
                {loading ? (
                  <>
                    <h1 className="bold-heading" id="stripe-return-heading">Checking your account status</h1>
                    <p className="discription-text" id="stripe-return-text">
                      One moment while we confirm things with Stripe.
                    </p>
                  </>
                ) : error ? (
                  <>
                    <h1 className="bold-heading" id="stripe-return-heading">We couldn't confirm your status</h1>
                    <p className="discription-text" id="stripe-return-text">
                      This is our status check failing, not your setup - anything you
                      submitted to Stripe is safe. Try again in a moment.
                    </p>
                    <div className="stripe-flow-actions" id="stripe-return-actions">
                      <button className="fill-btn" onClick={() => checkStatus()} id="stripe-return-retry-btn">Check Again</button>
                      <button
                        type="button"
                        className="stripe-flow-secondary-btn"
                        onClick={handleContinue}
                        id="stripe-return-continue-link"
                      >
                        Continue to SpotSaver
                        <span className="material-icons" aria-hidden="true">arrow_forward</span>
                      </button>
                    </div>
                  </>
                ) : payoutsEnabled ? (
                  <>
                    <h1 className="bold-heading" id="stripe-return-heading">Bank Account Connected</h1>
                    <p className="discription-text" id="stripe-return-text">
                      Your Stripe account is verified and ready to receive payouts.
                      Taking you to the next step...
                    </p>
                    <div className="stripe-flow-actions" id="stripe-return-actions">
                      <button className="fill-btn" onClick={handleContinue} id="stripe-return-continue-btn">Continue</button>
                    </div>
                  </>
                ) : detailsSubmitted ? (
                  <>
                    <h1 className="bold-heading" id="stripe-return-heading">Details Submitted</h1>
                    <p className="discription-text" id="stripe-return-text">
                      Stripe is verifying what you sent - this can take a little while.
                      We'll update your bank account status once it's done, so you can
                      carry on in the meantime.
                    </p>
                    <div className="stripe-flow-actions" id="stripe-return-actions">
                      <button className="fill-btn" onClick={() => checkStatus()} id="stripe-return-refresh-btn">Refresh Status</button>
                      <button
                        type="button"
                        className="stripe-flow-secondary-btn"
                        onClick={handleContinue}
                        id="stripe-return-continue-link"
                      >
                        Continue to SpotSaver
                        <span className="material-icons" aria-hidden="true">arrow_forward</span>
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <h1 className="bold-heading" id="stripe-return-heading">Bank Account Setup Pending</h1>
                    <p className="discription-text" id="stripe-return-text">
                      It looks like the setup wasn't finished. You can resume it any time
                      from here.
                    </p>
                    {/* Two real choices side by side: resuming is the recommended
                        one, but leaving is not a consolation-prize link - it says
                        where the client actually ends up. */}
                    <div className="stripe-flow-actions" id="stripe-return-actions">
                      <button className="fill-btn" onClick={handleResume} disabled={resuming} id="stripe-return-resume-btn">
                        {resuming ? "Redirecting to Stripe..." : "Resume Setup"}
                      </button>
                      <button
                        type="button"
                        className="stripe-flow-secondary-btn"
                        onClick={handleContinue}
                        id="stripe-return-continue-link"
                      >
                        Finish later, continue to SpotSaver
                        <span className="material-icons" aria-hidden="true">arrow_forward</span>
                      </button>
                    </div>
                    <p className="stripe-flow-note" id="stripe-return-note">
                      Stripe keeps your progress - resuming picks up where you left off.
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer className="login_footer" id="stripe-return-footer">
        <div className="container-fluid">
          <p className="footer-para" id="stripe-return-footer-text">
            © {new Date().getFullYear()} SpotSaver. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StripeOnboardingReturn;
